import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Link from '@material-ui/core/Link';
import Menu from '@material-ui/core/Menu';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ExpandMoreRoundedIcon from '@material-ui/icons/ExpandMoreRounded';
import ExtensionRoundedIcon from '@material-ui/icons/ExtensionRounded';
import HelpRoundedIcon from '@material-ui/icons/HelpRounded';
import LaptopChromebookRoundedIcon from '@material-ui/icons/LaptopChromebookRounded';
import { toggleSidebar } from 'actions/app';
import useLocalStorage from 'hooks/localStorage';
import useKeepLogRocketSessionAlive from 'hooks/refresh-logrocket-session';
import queryString from 'query-string';
import { getCurrentUser, isAuthenticated, logout } from 'services/Authentication';
import strings from 'strings';
import FPAvatar from 'UI/components/atoms/FPAvatar';
import FPIcon from 'UI/components/atoms/FPIcon';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import { When } from 'UI/components/atoms/When';
import { FPButtonMenu } from 'UI/components/molecules';
import GlobalSearchbar from 'UI/components/molecules/GlobalSearchbar';
import NotificationPreview from 'UI/components/organisms/NotificationPreview';
import { drawerAnchor, DrawerName } from 'UI/constants/defaults';
import { GpacLogo, SvgBurgerMenu } from 'UI/res';
import { SvgArtificialIntelligence } from 'UI/res/icons/filled';

import { IconList, MenuItems, NotificationButton, ThemeModeSelector } from './components';
import { styles, useStyles } from './styles';

const ARROW_SIZE = 32;

const chromeExtensionURL = `${
  (window.GPAC_ENV && window.GPAC_ENV.CHROME_EXTENSION_URL) ||
  process.env.REACT_APP_CHROME_EXTENSION_URL
}`;
const releaseNotesAccount =
  (window.GPAC_ENV && window.GPAC_ENV.RELEASE_NOTES_ACCOUNT) ||
  process.env.REACT_APP_RELEASE_NOTES_ACCOUNT;
const releaseNotesTag =
  (window.GPAC_ENV && window.GPAC_ENV.RELEASE_NOTES_TAGS) ||
  process.env.REACT_APP_RELEASE_NOTES_TAGS;

const MEDIUM_VIEWPORT = 1220;

const NavBar = () => {
  const { version, isSidebarOpen } = useSelector(({ app }) => app.ui);
  const dispatch = useDispatch();
  const isSmallViewPort = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));
  const classes = useStyles({
    isSmallViewPort
  });

  const searchBarRef = useRef(null);

  const user = isAuthenticated() && getCurrentUser();

  const history = useHistory();
  const location = useLocation();
  const { drawer } = queryString.parse(location.search);

  const [anchorEl, setAnchorEl] = useState(null);
  const [extensionIsViewed, setExtensionIsViewed] = useLocalStorage('extensionIsViewed', false);
  const [uiState, setUiState] = useState({
    areNotificationsOpen: drawer === DrawerName.Notifications || drawer === DrawerName.FeeAgreements
  });

  const handleOpenReleaseNotes = useCallback(
    () =>
      window.initReleaseNotes &&
      window.initReleaseNotes(releaseNotesAccount, releaseNotesTag, version),
    [version]
  );

  useEffect(() => {
    if (!releaseNotesAccount && !releaseNotesTag) return;

    handleOpenReleaseNotes();
  }, [handleOpenReleaseNotes]);

  useEffect(() => {
    isSmallViewPort ? dispatch(toggleSidebar(false)) : dispatch(toggleSidebar(true));
  }, [dispatch, isSmallViewPort]);

  const handleOpenMenuClick = event => setAnchorEl(event.currentTarget);

  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => logout();

  const handleExtensionButton = () => setExtensionIsViewed(true);

  const toggleDrawer = (activeDrawer, open) => event => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setUiState(prevState => ({ ...prevState, [activeDrawer]: open }));
  };

  const goHome = e => {
    e.preventDefault();
    history.push('/');
  };

  const handleToggleSidebar = () => dispatch(toggleSidebar(!isSidebarOpen));

  useKeepLogRocketSessionAlive(searchBarRef);

  const navActions = [
    {
      key: 'help',
      label: strings.navBar.help,
      href: 'https://gpac.freshservice.com/support/solutions/21000071629',
      icon: HelpRoundedIcon
    },
    {
      key: 'artificial-intelligence',
      label: strings.navBar.artificialIntelligenceButton,
      href: 'https://robot.fortpac.com/',
      badgeClasses: '',
      isInvisibleBadge: true,
      icon: SvgArtificialIntelligence
    },
    {
      key: 'extension',
      label: strings.navBar.extensionButton,
      hasBadge: true,
      href: chromeExtensionURL,
      badgeClasses: classes.fortPacExtension__dotBadge,
      isInvisibleBadge: extensionIsViewed,
      icon: ExtensionRoundedIcon,
      onClick: handleExtensionButton
    },
    {
      key: 'training',
      label: strings.navBar.trainingButton,
      href: 'https://training.gogpac.com/',
      icon: LaptopChromebookRoundedIcon
    }
  ];

  const icons = navActions.map(action => ({
    key: action.key,
    href: action.href,
    badgeClasses: action.badgeClasses,
    hasBadge: action.hasBadge,
    icon: action.icon,
    isInvisibleBadge: action.isInvisibleBadge,
    onClick: action.onClick,
    tooltipTitle: action.label
  }));

  const responsiveIcons = [
    ...navActions.map(action => ({
      key: action.key,
      title: action.label,
      visible: true,
      action: action.onClick,
      wrapper: 'a',
      target: '_blank',
      contactData: {
        href: action.href
      }
    })),
    {
      key: 'notifications',
      title: 'Notifications',
      visible: true,
      action: toggleDrawer('areNotificationsOpen', true)
    }
  ];

  const menuItems = [
    {
      shouldRender: !!releaseNotesAccount && !!releaseNotesTag,
      onClick: handleOpenReleaseNotes,
      className: 'release-notes',
      children: strings.navBar.releaseNotes,
      key: 'notes'
    },
    {
      shouldRender: true,
      onClick: handleLogout,
      className: classes.menuLink,
      children: strings.navBar.logOut,
      key: 'logout'
    },
    {
      shouldRender: true,
      onClick: null,
      className: classes.fortpacVersion,
      children: (
        <>
          FortPac <span>v{version}</span>
        </>
      ),
      key: 'version'
    }
  ];

  const isMediumViewPort = useMediaQuery(({ breakpoints }) => breakpoints.down(MEDIUM_VIEWPORT));

  const avatar = (
    <FPAvatar mode="user" userEmail={user?.email}>
      {user?.initials}
    </FPAvatar>
  );

  return (
    <Fragment key={drawerAnchor}>
      <Grid container className={classes.wrapper} alignItems="center">
        <Grid item xs="auto" md="auto">
          <div className={classes.logoMenuContainer}>
            <FPIconButton
              icon={SvgBurgerMenu}
              onClick={handleToggleSidebar}
              className={classes.menuContainer}
            />

            <Link href="/" onClick={goHome} className={classes.logoContainer}>
              <GpacLogo width={140} height={43} />
            </Link>
          </div>
        </Grid>
        <Grid item xs={5}>
          <GlobalSearchbar
            isSmallViewPort={isSmallViewPort}
            trackingMeta={{ origin: 'navbar' }}
            searchBarRef={searchBarRef}
          />
        </Grid>
        <Grid item xs="auto">
          <div className={classes.iconsAvatarContainer}>
            <When condition={isSmallViewPort}>
              <FPButtonMenu isIconButton menuItems={responsiveIcons} />
            </When>
            <When condition={!isSmallViewPort}>
              <div className={classes.iconListContainer}>
                <IconList icons={icons} />
                <NotificationButton onDrawerOpen={toggleDrawer('areNotificationsOpen', true)} />
              </div>
            </When>
            <div>
              <When condition={!isMediumViewPort}>
                <Button
                  onClick={handleOpenMenuClick}
                  color="primary"
                  className={classes.userCard}
                  classes={{ sizeSmall: classes.firstIcon }}
                  size="small"
                  startIcon={avatar}
                  endIcon={<FPIcon component={ExpandMoreRoundedIcon} size={ARROW_SIZE} />}
                >
                  {!isMediumViewPort && (
                    <div className={classes.name}>{user?.personalInformation?.first_name}</div>
                  )}
                </Button>
              </When>

              <When condition={isMediumViewPort}>
                <IconButton onClick={handleOpenMenuClick} aria-label="user">
                  {avatar}
                </IconButton>
              </When>

              <Menu
                id="simple-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference="none"
                PaperProps={{ style: styles.menuPaperStyles }}
                MenuListProps={{ style: styles.menuStyle }}
              >
                <ThemeModeSelector />
                <MenuItems menuItems={menuItems} />
              </Menu>
            </div>
          </div>
        </Grid>
      </Grid>

      <When condition={uiState.areNotificationsOpen}>
        <NotificationPreview onClose={toggleDrawer('areNotificationsOpen', false)} />
      </When>
    </Fragment>
  );
};

export default NavBar;
